import React from 'react';
import { useCSVStore, ValidationError, ValidationWarning } from '../../stores/csvStore';
import './CSVTable.css'; // We'll create this CSS file next
import { Box, Alert, Tooltip } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

interface ValidationStatus {
  type: 'error' | 'warning';
  messages: string[];
}

export const CSVTable: React.FC = () => {
  const { csvData, isLoading, validationResults, errors, warnings } = useCSVStore();

  if (isLoading) {
    return (
      <div className="csv-table-loading">
        <div className="loading-spinner"></div>
        <p>Loading CSV data...</p>
      </div>
    );
  }

  if (!csvData || csvData.length === 0) {
    return (
      <div className="csv-table-empty">
        <p>No CSV data available. Please upload a file.</p>
      </div>
    );
  }

  // Render validation results section
  const renderValidationStatus = () => {
    if (!validationResults) return null;
    
    return (
      <div className="validation-results">
        {errors.length > 0 && (
          <Alert severity="error" className="validation-alert">
            Found {errors.length} error{errors.length !== 1 ? 's' : ''} in the CSV file
          </Alert>
        )}
        
        {warnings.length > 0 && (
          <Alert severity="warning" className="validation-alert">
            Found {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
          </Alert>
        )}
        
        {errors.length === 0 && warnings.length === 0 && (
          <Alert severity="success" className="validation-alert">
            CSV file is valid with no errors or warnings
          </Alert>
        )}
      </div>
    );
  };

  // Check if a cell has validation issues
  const getCellValidationStatus = (rowIndex: number, colIndex: number): ValidationStatus | null => {
    if (!validationResults) return null;
    
    const cellErrors = errors.filter(
      (e: ValidationError) => e.row === rowIndex && e.column === colIndex.toString()
    );
    
    const cellWarnings = warnings.filter(
      (w: ValidationWarning) => w.row === rowIndex && w.column === colIndex.toString()
    );
    
    if (cellErrors.length > 0) return { type: 'error', messages: cellErrors.map(e => e.message) };
    if (cellWarnings.length > 0) return { type: 'warning', messages: cellWarnings.map(w => w.message) };
    return null;
  };

  return (
    <div className="csv-table-container">
      {renderValidationStatus()}
      
      <div className="csv-table-wrapper">
        <table className="csv-table">
          <thead>
            <tr>
              {csvData[0].map((header, index) => (
                <th key={index}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {csvData.slice(1).map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => {
                  const status = getCellValidationStatus(rowIndex + 1, colIndex);
                  const cellClass = status ? `cell-${status.type}` : '';
                  
                  return (
                    <td key={colIndex} className={cellClass}>
                      {cell}
                      {status && (
                        <Tooltip 
                          title={
                            <Box>
                              {status.messages.map((msg, i) => (
                                <p key={i}>{msg}</p>
                              ))}
                            </Box>
                          }
                        >
                          <Box component="span" className="validation-icon">
                            {status.type === 'error' ? <ErrorIcon color="error" fontSize="small" /> : <WarningIcon color="warning" fontSize="small" />}
                          </Box>
                        </Tooltip>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}; 